import { type SuppressKeyboardEventParams } from "ag-grid-community";

import {
  type DataGridAnyColDef,
  type DataGridColDef,
  type DataGridColGroupDef,
} from "./types";

const isElementVisible = (element: HTMLElement) => {
  if (element.hidden) return false;

  const style = getComputedStyle(element);

  return style.display !== "none" && style.visibility !== "hidden";
};

const getFocusableElementsInCell = (cell: Element) =>
  Array.from(
    cell.querySelectorAll<HTMLAnchorElement | HTMLButtonElement>("button, a"),
  ).filter(
    (element) =>
      !("disabled" in element && element.disabled) &&
      element.tabIndex !== -1 &&
      isElementVisible(element),
  );

/**
 * Allow Tab to move focus between the cell's action buttons,
 * but let ag-grid resume normal cell navigation once focus reaches the boundary button:
 * last button on Tab, first button on Shift+Tab.
 */
export const suppressActionsTabKey = ({
  event,
}: SuppressKeyboardEventParams) => {
  if (event.key !== "Tab") return false;

  const cell = (event.target as HTMLElement | null)?.closest(
    "[role='gridcell']",
  );

  if (!cell) return false;

  const focusableElements = getFocusableElementsInCell(cell);

  if (focusableElements.length === 0) return false;

  const activeElement = document.activeElement;
  const lastButton = focusableElements[focusableElements.length - 1];

  // Shift+Tab landed on the bare cell (no button focused yet) → pull focus into
  // the last button so backward navigation enters the cell from the end.
  const cellHasFocusedButton =
    cell.contains(activeElement) && activeElement !== cell;

  if (event.shiftKey && !cellHasFocusedButton) {
    lastButton.focus();
    event.preventDefault();

    return true;
  }

  return event.shiftKey
    ? activeElement !== focusableElements[0]
    : activeElement !== lastButton;
};
export const isColumnGroup = <TData>(
  col: DataGridAnyColDef<TData>,
): col is DataGridColGroupDef<TData> =>
  "children" in col && Array.isArray(col.children);
export const getColumnId = <TData>(col: DataGridColDef<TData>): string =>
  col.colId ?? col.field ?? "";
